import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { createUseStyles } from 'react-jss';
import type { match } from 'react-router-dom';
import type { History } from 'history';
import capitalize from 'capitalize';
import { Theme, ColorBadge, TextBox, Button, TrashIcon, AlertService, WasteIcon } from '@spoiler-alert/ui-library';
import client from '../../apollo/client';
import {
  AdminNumocoSingleOrderNaked,
  AdminPurchaseOrderByIdQuery,
  InvoiceStatus,
  NUMOCO_FREIGHT_PAYMENT_TERMS,
  NUMOCO_LOGISTICS_MODE,
  NUMOCO_SHIPMENT_INCOTERMS,
  NUMOCOOrderTerms,
  PO_STATUS,
  POLineItem,
  POStatus,
} from './po-queries';
import { VColumn, VTable } from '../../components/v-table';
import routePaths from '../../route-paths';
import type { VTableType } from '../../components/v-table/v-table-types';
import moment from 'moment';
import accounting from 'accounting';
import POManagerDetailLayout from './po-manager-detail-layout';
import { TitleService } from '../../services';
import { Breadcrumbs } from '../../store';
import DownloadButton from '../numoco-orders/download-button';
import appSettings from '../../app-settings';
import NewButton from '../import-manager/new-button';
import PaymentTermsModal from './payment-terms-modal';
import { AdminCreateNumocoOrder } from './po-mutations';
import { fetchWithToken } from '../../services/fetch-with-token';
import { getThemeByStatus, translateStatusLabel } from './po-status';

const useStyles = createUseStyles({
  wrap: {
    position: 'relative',
    '& h3': {
      margin: 0,
      fontSize: 18,
    },
    '& h4': {
      margin: 0,
    },
    '& h5': {
      margin: 0,
    },
  },
  addresses: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },
  address: {
    border: `1px solid ${Theme.tableBorderColor}`,
    padding: 10,
  },
  negative: {
    color: Theme.red,
  },
  table: {
    border: `1px solid ${Theme.tableBorderColor}`,
  },
  trucklane: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: [0, 0, 10],
  },
  trucklaneHeading: {
    fontSize: '18px',
    margin: 0,
  },
  siteName: {
    fontSize: '16px',
    margin: 0,
  },
  credit: {
    margin: [10, 0],
    fontSize: 14,
    textAlign: 'right',
  },
  poEntry: {
    height: 30,
    '& input': {
      textAlign: 'left',
      paddingLeft: 10,
    },
  },
  emailSection: {
    marginTop: 40,
  },
  emailRow: {
    display: 'flex',
    alignItems: 'center',
  },
  emailEntry: {
    '& input': {
      textAlign: 'left',
      paddingLeft: 10,
    },
  },
  poWrap: {
    marginTop: '1em',
  },
  poItems: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  poItem: {
    flexGrow: 1,
    border: `1px solid ${Theme.tableBorderColor}`,
    padding: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
  },
  orderTerms: {
    marginTop: 20,
  },
  bullets: {
    padding: [10, 0],
    fontSize: 14,
  },
  pageTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerStatus: {
    fontSize: 14,
  },
  downloads: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  actionItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  statusBox: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
});

export type BadgeTheme = 'grey' | 'green' | 'yellow' | 'orange' | 'teal' | 'red';

const POStatusTheme: Record<POStatus, BadgeTheme> = {
  [POStatus.PENDING]: 'yellow',
  [POStatus.ACCEPTED]: 'green',
  [POStatus.READY_TO_SEND]: 'grey',
};

const InvoiceStatusTheme: Record<InvoiceStatus, BadgeTheme> = {
  [InvoiceStatus.OPEN]: 'teal',
  [InvoiceStatus.VOIDED]: 'red',
  [InvoiceStatus.PAID]: 'green',
  [InvoiceStatus.PENDING]: 'yellow',
  [InvoiceStatus.DRAFT]: 'grey',
};

const defaultOrderTerms: NUMOCOOrderTerms = {
  is_prepaid: false,
  shipment_incoterms: NUMOCO_SHIPMENT_INCOTERMS.DDP,
  shipment_method: 'LTL',
  shipment_freight_payment_terms: NUMOCO_FREIGHT_PAYMENT_TERMS.FREIGHT_COLLECT,
  logistics_mode: NUMOCO_LOGISTICS_MODE.PLATFORM,
  take_rate_basis_points: 1500,
  supplier_payment_due_days: 45,
  buyer_payment_due_days: 30,
};

const InProgressDetail: React.FC<{ history: History; match: match<{ purchaseOrderId: string }> }> = ({
  history,
  match,
}) => {
  const classes = useStyles();

  const purchaseOrderId = match.params.purchaseOrderId;

  const [generating, setGenerating] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [orderTerms, setOrderTerms] = useState<NUMOCOOrderTerms>(defaultOrderTerms);

  const [createOrder, { loading: creatingOrder }] = useMutation(AdminCreateNumocoOrder);

  const {
    data,
    loading,
    refetch: refetchPurchaseOrder,
  } = useQuery(AdminPurchaseOrderByIdQuery, { variables: { purchaseOrderId } });

  const purchaseOrder = data?.adminPurchaseOrderByIdQuery || null;

  const {
    data: numocoOrderData,
    loading: loadingNumocoOrder,
    refetch: refetchNumocoOrder,
  } = useQuery(AdminNumocoSingleOrderNaked, {
    variables: { orderId: purchaseOrder?.externalOrderId || '' },
    skip: !purchaseOrder?.externalOrderId,
  });

  const numocoOrder = numocoOrderData?.adminNumocoSingleOrderNaked || null;

  useEffect(() => {
    TitleService.setTitles('Sites Administration');
    Breadcrumbs.set([
      {
        url: `${routePaths.purchaseOrderManager}#in-progress`,
        title: 'Purchase Order Administration',
      },
      {
        url: `${routePaths.purchaseOrderManager}/in-progress/${purchaseOrderId}`,
        title: 'Order Detail',
      },
    ]);
  }, [purchaseOrderId]);

  const orderTotal = useMemo(() => {
    return purchaseOrder?.lineItems.reduce((sum, line) => sum + line.totalPrice, 0) || 0;
  }, [purchaseOrder?.lineItems]);

  useEffect(() => {
    if (purchaseOrder?.transactionServicesSettings) {
      const { logisticsTerm, transactionServicesSettings, availableCredit } = purchaseOrder;
      const { packingListContacts, takeRate, supplierPaymentDueDays, buyerPaymentDueDays } =
        transactionServicesSettings;
      setEmailList([...packingListContacts]);
      const creditInadequate = orderTotal >= availableCredit;
      const _orderTerms = {
        ...defaultOrderTerms,
        logistics_mode: logisticsTerm === 'PICKUP' ? NUMOCO_LOGISTICS_MODE.BUYER : NUMOCO_LOGISTICS_MODE.SUPPLIER,
        take_rate_basis_points: takeRate,
        supplier_payment_due_days: supplierPaymentDueDays,
        buyer_payment_due_days: creditInadequate ? 0 : buyerPaymentDueDays,
        is_prepaid: creditInadequate,
      };
      setOrderTerms(_orderTerms);
    }
  }, [purchaseOrder, orderTotal]);

  const changeEmailValue = useCallback(
    (value: string, index: number) => {
      const newEmailList = Array.from(emailList);
      newEmailList[index] = value;
      setEmailList(newEmailList);
    },
    [emailList],
  );

  const addNewEmail = useCallback(() => {
    const newEmailList = Array.from(emailList);
    newEmailList.push('');
    setEmailList(newEmailList);
  }, [emailList]);

  const deleteEmail = useCallback(
    (index: number) => {
      const newEmailList = Array.from(emailList);
      newEmailList.splice(index, 1);
      setEmailList(newEmailList);
    },
    [emailList],
  );

  const resetTerms = () => {
    if (purchaseOrder) {
      const { availableCredit, logisticsTerm, transactionServicesSettings } = purchaseOrder;
      const { takeRate, supplierPaymentDueDays, buyerPaymentDueDays } = transactionServicesSettings;
      const creditInadequate = orderTotal >= availableCredit;
      const _orderTerms = {
        ...defaultOrderTerms,
        logistics_mode: logisticsTerm === 'PICKUP' ? NUMOCO_LOGISTICS_MODE.BUYER : NUMOCO_LOGISTICS_MODE.SUPPLIER,
        take_rate_basis_points: takeRate,
        supplier_payment_due_days: supplierPaymentDueDays,
        buyer_payment_due_days: buyerPaymentDueDays,
        is_prepaid: creditInadequate,
      };
      setOrderTerms(_orderTerms);
    } else {
      setOrderTerms(defaultOrderTerms);
    }
  };

  const preGeneratedReconciliationForm = useMemo(() => {
    return purchaseOrder?.fileAttachments.find((file) => file.type === 'Reconciliation Sheet Form');
  }, [purchaseOrder?.fileAttachments]);

  const handleSendPackingList = useCallback(async () => {
    if (!purchaseOrder || !numocoOrder) return;
    setGenerating(true);
    try {
      const result = await fetchWithToken(`${appSettings.GRAPHQL_SERVER_URL}numoco/reconciliation-form`, {
        body: JSON.stringify({
          sellerSiteId: purchaseOrder.sellerSiteId,
          buyerSiteId: purchaseOrder.buyerSiteId,
          packingListUrl: numocoOrder.packing_list_pdf_url,
          numocoOrderNumber: numocoOrder.order_number,
          emailList: emailList,
          purchaseOrderId: purchaseOrder._id,
        }),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (result.ok) {
        const body = await result.json();
        if (!(body?.errors.length === 0)) throw new Error(body.results.errors[0]);
        AlertService.alert({
          type: 'info',
          message: <span>Email was successfully sent to {body.results.contacts.join(', ')}</span>,
          autoDismiss: true,
          dismissDelay: 5000,
        });
        refetchPurchaseOrder();
      }
    } catch (error) {
      AlertService.alert({
        type: 'warning',
        message: <span>{error}</span>,
        autoDismiss: false,
        dismissDelay: 5000,
      });
    }
    setGenerating(false);
  }, [purchaseOrder, numocoOrder, emailList, refetchPurchaseOrder]);

  const submitOrder = useCallback(async () => {
    try {
      if (!purchaseOrder) throw new Error('No purchase order');
      const result = await createOrder({
        variables: {
          purchaseOrderId: purchaseOrder._id,
          orderTerms,
        },
      });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'adminAssignedPOQuery' });
      if (result.data?.adminCreateNumocoOrder.errors && result.data.adminCreateNumocoOrder.errors.length > 0) {
        throw new Error(result.data.adminCreateNumocoOrder.errors[0].message);
      }
    } catch (error) {
      AlertService.alert({
        type: 'warning',
        message: (error as Error).message,
        autoDismiss: false,
        dismissDelay: 1000,
      });
    }
  }, [createOrder, purchaseOrder, orderTerms]);

  const documents = useMemo(() => {
    let numocoDocuments: JSX.Element[] = [];
    if (numocoOrder) {
      numocoDocuments = numocoOrder.documents
        ? numocoOrder.documents.map((doc) => (
            <div key={doc.type}>
              <DownloadButton
                kind={capitalize(doc.type.replace(/_/g, ' '), false)}
                path={doc.path}
                downloadPath={doc.url}
              />
            </div>
          ))
        : [];
      if (numocoOrder.packing_list_pdf_url) {
        numocoDocuments.unshift(
          <div key="packing-list">
            <DownloadButton
              kind="Packing List"
              fileName={`${numocoOrder.name}-Packing_List.pdf`}
              downloadPath={numocoOrder.packing_list_pdf_url}
            />
          </div>,
        );
      }
    }
    if (purchaseOrder) {
      return (
        <div>
          <h3>Documents</h3>
          <div className={classes.downloads}>
            {numocoDocuments}
            {purchaseOrder.fileAttachments.map(({ type, path }) => (
              <div key={path}>
                <DownloadButton
                  kind={type}
                  path={path}
                  downloadPath={`${appSettings.GRAPHQL_SERVER_URL}download/?path=${encodeURIComponent(path)}&source=FTP`}
                />
              </div>
            ))}
          </div>
        </div>
      );
    }
    return '';
  }, [classes, purchaseOrder, numocoOrder]);

  const middleLeft = useMemo(() => {
    if (purchaseOrder) {
      const { sellerLocation, sellerName } = purchaseOrder;
      return (
        <div>
          <h3>Seller: {sellerName}</h3>
          <div className={classes.addresses}>
            <div className={classes.address}>
              <div>{sellerLocation.siteName}</div>
              <div>{sellerLocation.physicalAddress.streetAddress}</div>
              <div>
                {sellerLocation.physicalAddress.city}, {sellerLocation.physicalAddress.stateCode}{' '}
                {sellerLocation.physicalAddress.zipCode}
              </div>
              <div>{sellerLocation.physicalAddress.countryCode}</div>
            </div>
          </div>
          <div className={classes.emailSection}>
            <h4>Seller Contacts</h4>
            {emailList.map((p, index) => {
              return (
                <div key={p} className={classes.emailRow}>
                  <TextBox
                    key={`textbox-${index}`}
                    className={classes.emailEntry}
                    value={p}
                    onChange={(value: string) => changeEmailValue(value, index)}
                    small
                  />
                  <Button
                    key={`btn-${index}`}
                    warning
                    resting={true}
                    icon={
                      TrashIcon as React.FC<{
                        style?: React.CSSProperties;
                        className?: string;
                      }>
                    }
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      deleteEmail(index);
                    }}
                  />
                </div>
              );
            })}
            <div>
              <NewButton onClick={addNewEmail}>New</NewButton>
            </div>
          </div>
        </div>
      );
    }
    return '';
  }, [classes, purchaseOrder, emailList, addNewEmail, changeEmailValue, deleteEmail]);

  const middleRight = useMemo(() => {
    if (purchaseOrder) {
      const { buyerLocation, buyerName, creditLimit, availableCredit } = purchaseOrder;
      return (
        <div>
          <h3>Buyer: {buyerName}</h3>
          <div className={classes.addresses}>
            <div className={classes.address}>
              <div>{buyerLocation.siteName}</div>
              <div>{buyerLocation.physicalAddress.streetAddress}</div>
              <div>
                {buyerLocation.physicalAddress.city}, {buyerLocation.physicalAddress.stateCode}{' '}
                {buyerLocation.physicalAddress.zipCode}
              </div>
              <div>{buyerLocation.physicalAddress.countryCode}</div>
            </div>
          </div>
          <div className={classes.credit}>
            <h4>Order vs Credit</h4>
            <div>Credit Limit: {accounting.formatMoney(creditLimit)}</div>
            <div className={availableCredit - orderTotal > 0 ? '' : classes.negative}>
              Available Credit: {accounting.formatMoney(availableCredit)}
            </div>
            <div>Order Total: {accounting.formatMoney(orderTotal)}</div>
          </div>
        </div>
      );
    }
    return '';
  }, [classes, purchaseOrder, orderTotal]);

  const middleCenter = useMemo(() => {
    if (numocoOrder) {
      const uploadPOUrl = `https://control.newmoneycompany.com/billing/orders/${numocoOrder._id}/buyer-po?detail=upload-po`;
      return (
        <div>
          <h3>Purchase Orders</h3>
          {numocoOrder.purchase_orders.length === 0 && (
            <div>
              <Button onClick={() => window.open(uploadPOUrl, '_blank', 'noopener,noreferrer')}>Upload Buyer PO</Button>
            </div>
          )}
          <div className={classes.poItems}>
            {numocoOrder.purchase_orders.map((po) => {
              let suffix = '';
              switch (po.type) {
                case 'BUYER':
                  suffix = 'buyer-po';
                  break;
                case 'NMC':
                  suffix = 'nmc-po';
                  break;
                case 'SUPPLIER':
                  suffix = `supplier-po/${po.id}`;
                  break;
                default:
              }
              const poLink = `https://control.newmoneycompany.com/billing/orders/${numocoOrder._id}/${suffix}`;
              return (
                <div key={po.id} className={classes.poItem}>
                  <div>
                    <h4>
                      {capitalize(po.type, false)} PO #{po.id}
                    </h4>
                  </div>
                  <div>
                    <ColorBadge id={po.id} theme={POStatusTheme[po.status]}>
                      {po.status.replace(/_/g, ' ')}
                    </ColorBadge>
                  </div>
                  <div>
                    <Button
                      secondary={po.status !== POStatus.READY_TO_SEND}
                      onClick={() => window.open(poLink, '_blank', 'noopener,noreferrer')}
                    >
                      View PO
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className={classes.poWrap}>
            <h3>Invoices</h3>
            {numocoOrder.invoices.length === 0 && <div>No Invoices</div>}
            <div className={classes.poItems}>
              {numocoOrder.invoices.map((inv) => (
                <div key={inv.id} className={classes.poItem}>
                  <div>
                    <h4>#{inv.invoice_number}</h4>
                    <div>Due Date: {moment(inv.due_date).format('MMM d, YYYY')}</div>
                    {inv.close_time && <div>Close Time: {moment(inv.close_time).format('MMM d, YYYY')}</div>}
                  </div>
                  <div>
                    <div>Total Amount: {accounting.formatMoney(inv.total_amount)}</div>
                    <div>Was Sent? {inv.was_sent ? 'Yes' : 'No'}</div>
                  </div>
                  <div>
                    <ColorBadge id={inv.id} theme={InvoiceStatusTheme[inv.status]}>
                      {inv.status}
                    </ColorBadge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    return '';
  }, [classes, numocoOrder]);

  const table = useMemo(() => {
    const columns = [
      new VColumn({
        field: 'description',
        displayName: 'Description',
        sortable: true,
      }),
      new VColumn({
        field: 'itemName',
        displayName: 'ItemName',
        sortable: true,
      }),
      new VColumn({
        field: 'acceptanceDate',
        displayName: 'Acceptance Date',
        sortable: true,
        formatter: (value) => moment(value).format('MMM D, YYYY'),
      }),
      new VColumn({
        field: 'externalTransactionId',
        displayName: 'External Transaction Id',
        sortable: true,
      }),
      new VColumn({
        field: 'unitPrice',
        displayName: 'Unit Price',
        sortable: true,
        formatter: (value) => accounting.formatMoney(value),
      }),
      new VColumn({
        field: 'quantity',
        displayName: 'Quantity',
        sortable: true,
      }),
      new VColumn({
        field: 'totalPrice',
        displayName: 'Total Price',
        sortable: true,
        defaultSort: true,
        defaultSortDirection: 'desc',
        formatter: (value) => accounting.formatMoney(value),
      }),
    ];
    const AwaitingPODetailTable = VTable as VTableType<POLineItem>;
    return (
      <div className={classes.table}>
        <AwaitingPODetailTable cols={columns} data={purchaseOrder?.lineItems || []} loading={loading} />
      </div>
    );
  }, [classes, purchaseOrder, loading]);

  const actions = useMemo(() => {
    if (purchaseOrder?.externalOrderId) {
      if (numocoOrder) {
        const url = `https://control.newmoneycompany.com/billing/orders/${numocoOrder._id}/overview`;
        return (
          <div>
            <h3>Actions</h3>
            <div>
              <ul className={classes.bullets}>
                <li>PO Submitted: {moment(purchaseOrder?.poAssignedDate).format('MMM D, YYYY')}</li>
                <li>Order Created: {moment(numocoOrder.created_at).format('MMM D, YYYY')}</li>
              </ul>
            </div>
            <div className={classes.actionItems}>
              {numocoOrder.packing_list_pdf_url && (
                <Button onClick={handleSendPackingList} loading={generating} loadingText="Sending...">
                  {preGeneratedReconciliationForm ? 'Resend' : 'Send'} Packing List & Rec Form
                </Button>
              )}
              <Button secondary onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
                View Order in Numoco
              </Button>
            </div>
          </div>
        );
      }
      return null;
    }
    if (purchaseOrder) {
      return (
        <div>
          <h3>Actions</h3>
          PO Submitted: {moment(purchaseOrder?.poAssignedDate).format('MMM D, YYYY')}
          <div className={classes.actionItems}>
            <Button
              onClick={submitOrder}
              loading={creatingOrder}
              loadingText="Submitting..."
              disabled={purchaseOrder?.status !== PO_STATUS.ASSIGNED}
            >
              Submit Order to Numoco
            </Button>
          </div>
          <div className={classes.orderTerms}>
            <h4>Terms</h4>
            <ul className={classes.bullets}>
              <li>
                <strong>INCO Terms:</strong> {orderTerms.shipment_incoterms}
              </li>
              <li>
                <strong>Shipment Method:</strong> {orderTerms.shipment_method}
              </li>
              <li>
                <strong>Freight Terms:</strong> {orderTerms.shipment_freight_payment_terms}
              </li>
              <li>
                <strong>Logistics Mode:</strong> {orderTerms.logistics_mode}
              </li>
              <li>
                <strong>Prepaid:</strong> {orderTerms.is_prepaid ? 'Yes' : 'No'}
              </li>
              <li>
                <strong>Take Rate:</strong> {(orderTerms.take_rate_basis_points || 0) / 100}%
              </li>
              <li>
                <strong>Supplier Payment Due:</strong> {orderTerms.supplier_payment_due_days} days
              </li>
              <li>
                <strong>Buyer Payment Due:</strong> {orderTerms.buyer_payment_due_days} days
              </li>
            </ul>
            <Button secondary onClick={() => setShowTermsModal(true)}>
              Edit Terms
            </Button>
          </div>
        </div>
      );
    }
    return null;
  }, [
    classes,
    purchaseOrder,
    numocoOrder,
    orderTerms,
    preGeneratedReconciliationForm,
    handleSendPackingList,
    creatingOrder,
    generating,
    submitOrder,
  ]);

  const title = useMemo(() => {
    const refetch = async () => {
      await Promise.all([refetchPurchaseOrder(), refetchNumocoOrder()]);
    };
    return (
      <div className={classes.pageTitle}>
        <span>Managed Marketplace Purchase Order #{purchaseOrder?.poNumber}</span>
        <div className={classes.statusBox}>
          {purchaseOrder && (
            <ColorBadge id={purchaseOrder?._id} theme={getThemeByStatus(purchaseOrder.status)}>
              {translateStatusLabel(purchaseOrder.status)}
            </ColorBadge>
          )}
          {numocoOrder && (
            <>
              <span className={classes.headerStatus}>
                Payment Status:{' '}
                <ColorBadge id="numoco-status" theme={'green'}>
                  {numocoOrder.status}
                </ColorBadge>
              </span>
              <Button
                icon={WasteIcon as unknown as FunctionComponent}
                secondary={!!numocoOrder.packing_list_pdf_url}
                loading={loadingNumocoOrder || loading}
                loadingText="Fetching..."
                onClick={refetch}
              >
                Refresh
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }, [purchaseOrder, numocoOrder, classes, loadingNumocoOrder, refetchNumocoOrder, refetchPurchaseOrder, loading]);

  return (
    <div className={classes.wrap}>
      <POManagerDetailLayout
        loading={loading || loadingNumocoOrder || creatingOrder || generating}
        title={title}
        table={table}
        documents={documents}
        actions={actions}
        middleLeft={middleLeft}
        middleRight={middleRight}
        middleCenter={middleCenter}
      />
      <PaymentTermsModal
        terms={orderTerms}
        setTerms={setOrderTerms}
        showTermsModal={showTermsModal}
        setShowTermsModal={setShowTermsModal}
        resetTerms={resetTerms}
      />
    </div>
  );
};

export default InProgressDetail;
